import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Batch, Workflow } from '@couch/shared';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';

interface Props { recipeId: string; machineId: string; workflow: Workflow; version: number | undefined }

const viewUrl = (machineId: string, name: string) => `/api/v1/machines/${machineId}/inputs/view?name=${encodeURIComponent(name)}`;

/** The batch's pictures and their results, polled until every run has finished. */
function BatchResults({ recipeId, initial }: { recipeId: string; initial: Batch }) {
  const queryClient = useQueryClient();
  const batch = useQuery({
    queryKey: ['batch', initial.id], initialData: { batch: initial },
    queryFn: async () => {
      const res = await api.getBatch(recipeId, initial.id);
      if (res.batch.done) void queryClient.invalidateQueries({ queryKey: ['runs', recipeId] });
      return res;
    },
    refetchInterval: (q) => (q.state.data?.batch.done ? false : 2000),
  });
  const { items, done } = batch.data.batch;
  const finished = items.filter((i) => ['succeeded', 'failed', 'cancelled'].includes(i.run.status)).length;
  return (
    <div>
      <p role="status">{done ? `Done — ${items.length} pictures.` : `Running ${finished} of ${items.length} done…`}</p>
      <ul className="batch-grid">
        {items.map(({ image, run }) => (
          <li key={run.id}>
            <span className="muted">{image}</span>
            {run.images[0] ? <a href={run.images[0].url} target="_blank" rel="noreferrer"><img src={run.images[0].url} alt={`Result for ${image}`} /></a>
              : <span className={run.status === 'failed' ? 'form-error' : 'muted'}>{run.status === 'failed' ? run.error ?? 'failed' : run.status}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Runs the recipe once per picture, same settings and seed — sequences, one person in many poses (doc 39). */
export function BatchPanel({ recipeId, machineId, workflow, version }: Props) {
  const loaders = Object.entries(workflow).filter(([, node]) => node.class_type === 'LoadImage').map(([id]) => id);
  const inputs = useQuery({ queryKey: ['inputs', machineId], queryFn: () => api.listInputs(machineId), enabled: loaders.length > 0 && machineId !== '' });
  const [chosen, setChosen] = useState<string[]>([]);
  const [nodeOverride, setNodeOverride] = useState('');
  const nodeId = nodeOverride || loaders[0] || '';
  const queryClient = useQueryClient();
  const upload = useMutation({
    mutationFn: async (files: File[]) => { for (const file of files) { const { image } = await api.uploadInput(machineId, file); setChosen((list) => [...list, image.name]); } },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['inputs', machineId] }),
  });
  const start = useMutation({ mutationFn: () => api.startBatch(recipeId, { images: chosen, inputNodeId: nodeId, machineId, ...(version ? { version } : {}) }) });
  if (loaders.length === 0 || machineId === '') return null;
  const toggle = (name: string) => { setChosen((list) => (list.includes(name) ? list.filter((n) => n !== name) : [...list, name])); };
  return (
    <section className="panel" aria-labelledby="batch-title">
      <h2 id="batch-title">Run over several pictures</h2>
      <p className="muted">Same settings and seed for every picture — for sequences, or one person in many pictures.</p>
      {loaders.length > 1 && (
        <label>Picture input{' '}
          <select value={nodeId} onChange={(e) => { setNodeOverride(e.target.value); }}>{loaders.map((id) => <option key={id} value={id}>node {id}</option>)}</select>
        </label>
      )}
      <fieldset className="batch-picker" aria-label="Pictures">
        {(inputs.data?.images ?? []).map((img) => (
          <label key={img.name} className={chosen.includes(img.name) ? 'batch-chosen' : ''}>
            <input type="checkbox" checked={chosen.includes(img.name)} onChange={() => { toggle(img.name); }} />
            <img src={img.url} alt="" loading="lazy" /><span>{img.name}</span>
          </label>
        ))}
      </fieldset>
      <p>
        <label className="button-link">{upload.isPending ? 'Uploading…' : 'Upload pictures'}
          <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="visually-hidden" disabled={upload.isPending}
            onChange={(e) => { const files = [...(e.target.files ?? [])]; if (files.length) upload.mutate(files); e.target.value = ''; }} />
        </label>{' '}
        <button type="button" disabled={chosen.length === 0 || start.isPending || upload.isPending} onClick={() => { start.mutate(); }}>
          Run on {chosen.length} picture{chosen.length === 1 ? '' : 's'}
        </button>
      </p>
      {[upload.error, start.error].filter(Boolean).map((e, n) => <p key={n} role="alert" className="form-error">{errorMessage(e)}</p>)}
      {start.data && <BatchResults key={start.data.batch.id} recipeId={recipeId} initial={start.data.batch} />}
      {chosen.length > 0 && !start.data && <p className="muted">Chosen: {chosen.map((n) => <img key={n} className="batch-thumb" src={viewUrl(machineId, n)} alt={n} />)}</p>}
    </section>
  );
}
